import React, { useState, useEffect, useCallback } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  makeStyles,
  Box,
  Paper,
  Button,
  Snackbar,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowBack from "@material-ui/icons/ArrowBack";
import {
  StatusSelection,
  AllocationConfigurations,
  OtherConfigurations,
  ProcessFlowConfiguration,
  DocumentDeliverables,
  ShipmentConfigurations,
  ReviewAndAddProduct,
} from "./steps";
import ProductDetails from "./steps/ProductDetails";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { useIsMounted } from "../../../../hooks/useIsMounted";

// Helper function to check if product is a file uploaded product
function checkFileUploadedProduct(productFormData) {
  const hasMaterialProducts = productFormData?.materialDetails?.productMaterialsList && 
                             productFormData.materialDetails.productMaterialsList.length > 0;
  const isFgStatusesEmpty = !productFormData?.productFGStatuses?.fgStatusesJSON || 
                           Object.keys(productFormData.productFGStatuses.fgStatusesJSON).length === 0;
  return hasMaterialProducts && isFgStatusesEmpty;
}

// Helper function to convert allocation configuration trigger values from status names to IDs
function convertAllocationConfigTriggerValues(allocationConfig, poStatuses) {
  if (!allocationConfig || !poStatuses || !Array.isArray(poStatuses)) {
    return allocationConfig;
  }

  const converted = { ...allocationConfig };

  // Helper to convert status name to ID
  const getStatusIdByName = (statusName) => {
    if (!statusName || typeof statusName !== 'string') return statusName;
    const status = poStatuses.find(s => s.status_name === statusName);
    return status ? status.status_id : statusName;
  };

  // Convert trigger values if their corresponding trigger type is "poStatus"
  if (converted.sponsorTriggerStart === "poStatus" && converted.sponsorTriggerStartValue) {
    converted.sponsorTriggerStartValue = getStatusIdByName(converted.sponsorTriggerStartValue);
  }
  
  if (converted.sponsorTriggerEnd === "poStatus" && converted.sponsorTriggerEndValue) {
    converted.sponsorTriggerEndValue = getStatusIdByName(converted.sponsorTriggerEndValue);
  }
  
  if (converted.cmoTriggerStart === "poStatus" && converted.cmoTriggerStartValue) {
    converted.cmoTriggerStartValue = getStatusIdByName(converted.cmoTriggerStartValue);
  }
  
  if (converted.cmoTriggerEnd === "poStatus" && converted.cmoTriggerEndValue) {
    converted.cmoTriggerEndValue = getStatusIdByName(converted.cmoTriggerEndValue);
  }

  return converted;
}


const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  stepper: {
    maxWidth: "90%",
    margin: "0 auto",
    padding: "50px 24px",
  },
  stepCompleted: {
    color: "#4CAF50",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    color: "#ec6535",
    marginBottom: "1rem",
    marginTop: "1rem",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  formTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 500,
  },
  button: {
    minWidth: 180,
    // fontWeight: 600,
    // fontSize: 16,
    borderRadius: 30,
    // height: 44,
  },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  select: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  tableCell: {
    "& .MuiSelect-root": {
      backgroundColor: "#fff",
    },
    padding: theme.spacing(1),
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  stepLabel: {
    flexDirection: "column",
    fontSize: 15,
    position: "relative",
    "& .MuiStepLabel-iconContainer": {
      padding: 0,
    },
    "& .MuiStepLabel-labelContainer": {
      width: "auto",
      minWidth: 140,
      position: "absolute",
      left: "50%",
      top: 30,
      transform: " translateX(-50%)",
      textAlign: "center",
    },
  },
}));

const steps = [
  "Product Details",
  "Status Selection",
  "Allocation Configurations",
  "Other Configurations",
  "Process Flow Configurations",
  "Document Deliverables",
  "Review And Add Product",
]; //  "Document Deliverables",

class ProductConfigErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ProductConfig Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="error">
            An error occurred while loading the product configuration. Please
            try again.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              this.setState({ hasError: false });
              // Reset to first step
              sessionStorage.setItem("currentStep", "0");
              // Force component refresh
              window.location.reload();
            }}
            style={{ marginTop: 16 }}
          >
            Retry
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const ProductConfigStepper = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const isMounted = useIsMounted();
  const { roleName, organizationName, ecosystemId } = useParams();
  const [skipReplicateDialog, setSkipReplicateDialog] = useState(false);


  // State initialization with safety defaults
  const [activeStep, setActiveStep] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [savedSteps, setSavedSteps] = useState(new Set()); // Track which steps have been saved
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const selectedSupplytype = location.state?.selectedSupplytype;
  const [formData, setFormData] = useState({});
  const [apiCallSuccessful, setApiCallSuccessful] = useState(false);
  // Load persisted form data when component mounts
  useEffect(() => {
    try {
      // Check if we have a selected product from the previous screen
      const selectedProduct = location.state?.selectedProduct;
      const selectedProductFormData = location.state?.selectedProductFormData;
      // const materialDetails = location.state?.materialDetails;


      if (selectedProductFormData) {
        // Check for file uploaded product: materialDetails has products AND productFGStatuses is empty
        const isFileUploadedProduct = checkFileUploadedProduct(selectedProductFormData);

        
        // Set file uploaded product flag in localStorage
        if (isFileUploadedProduct) {
          localStorage.setItem("fileUploadedProduct", "true");
        } else {
          localStorage.removeItem("fileUploadedProduct");
        }
        
        // Get PO statuses for conversion
        const poStatuses = selectedProductFormData?.productFGStatuses?.fgStatusesJSON?.poStatuses || [];
        
        // Convert allocation configuration trigger values from names to IDs
        const convertedAllocationConfig = convertAllocationConfigTriggerValues(
          selectedProductFormData?.allocationConfiguration,
          poStatuses
        );
        
        // Set form data from the API response
        setFormData({
          productDetails: selectedProductFormData?.productDetails,
          materialDetails: selectedProductFormData?.materialDetails || null,
          fgStatuses: selectedProductFormData?.productFGStatuses?.fgStatusesJSON || {},
          documentDeliverables: selectedProductFormData?.documentDetailsList || [],
          otherConfiguration: selectedProductFormData?.otherConfiguration || {},
          processFlowConfiguration: selectedProductFormData?.processFlowConfiguration || {},
          allocationConfiguration: convertedAllocationConfig || {},
        });
      } 
      else if (location.state?.productDetails && location.state?.materialDetails) {
        // Handle case where productDetails and materialDetails are passed separately (from ClinicalSupply)
       
        setFormData({
          productDetails: location.state.productDetails,
          materialDetails: location.state.materialDetails,
          fgStatuses: {},
          documentDeliverables: [],
          otherConfiguration: {},
          processFlowConfiguration: {},
          allocationConfiguration: {},
        });
      } 
      else if (selectedProduct) {
        // Update the form data with the selected product info
        setFormData((prevData) => ({
          ...prevData,
          productCode: selectedProduct.sponsorProductCode,
          productDescription: selectedProduct.sponsorProductDescription,
        }));
      }

      // Restore current step
      const savedStep = sessionStorage.getItem("currentStep");
      if (savedStep !== null) {
        const stepIndex = parseInt(savedStep, 10);
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex < steps.length) {
          setActiveStep(stepIndex);
        }
      }

      setIsInitialized(true);
    } catch (error) {
      console.error("Error loading persisted form data:", error);
      setHasErrors(true);
    }
  }, [location.state]); // Add location.state as dependency

  // On mount, restore formData from sessionStorage if it exists
  useEffect(() => {
    const savedFormData = sessionStorage.getItem("productConfigFormData");
    if (savedFormData) {
      setFormData(JSON.parse(savedFormData));
    }
  }, []);

  const isEdit = location.state?.isEdit;
  const isPending = location.state?.isPending;
  const selectedProductFormData = location.state?.selectedProductFormData;

  // Add new useEffect for handling pending product edit
  useEffect(() => {
    try {
      const isEdit = location.state?.isEdit;
      const isPending = location.state?.isPending;
      const selectedProductFormData = location.state?.selectedProductFormData;

      // Check if there's already a saved step in sessionStorage
      const savedStep = sessionStorage.getItem("currentStep");
      
      // Only proceed with step determination if there's no saved step
      // This prevents overwriting the user's current position on page refresh
      if (savedStep !== null) {
        return; // Exit early if step is already saved
      }

      // If editing a pending product, determine the active step based on response data
      if (isEdit && isPending && selectedProductFormData) {
        const { productFGStatuses, documentDetailsList, allocationConfiguration, otherConfiguration, processFlowConfiguration, materialDetails } = selectedProductFormData;

        // File uploaded product: Check if materialDetails has products AND productFGStatuses is empty
        // This indicates we need to go to first step (Product Details)
        const isFileUploadedProduct = checkFileUploadedProduct(selectedProductFormData);
        
        if (isFileUploadedProduct && !(sessionStorage.getItem("selectionForNewProduct"))) {
          // File uploaded product: go to first step (Product Details)
          setActiveStep(0);
          sessionStorage.setItem("currentStep", "0");
        }
        // Check if productFGStatuses is empty (but not the special case above)
        else if (!productFGStatuses?.fgStatusesJSON || Object.keys(productFGStatuses.fgStatusesJSON).length === 0) {
          // Set active step to 1 (Status Selection)
          setActiveStep(1);
          sessionStorage.setItem("currentStep", "1");
        }
        // Check if documentDetailsList is empty
        else if (!allocationConfiguration || allocationConfiguration.allowLotAllocationBy === null) {
          // Set active step to 2 (Document Deliverables)
          setActiveStep(2);
          sessionStorage.setItem("currentStep", "2");
        }
        // Check if poConfiguration is empty
        else if (!otherConfiguration || !otherConfiguration.mroDateFieldCalculation) {
          // Set active step to 3 (PO Configurations)
          setActiveStep(3);
          sessionStorage.setItem("currentStep", "3");
        }
        // Check if processFlowConfiguration is empty
        else if (!processFlowConfiguration || processFlowConfiguration.stockCheckReq === null) {
          // Set active step to 4 (Batch Configurations)
          setActiveStep(4);
          sessionStorage.setItem("currentStep", "4");
        }
        // Check if shipmentConfiguration is empty
        else if (!documentDetailsList || documentDetailsList.length === 0) {
          // Set active step to 5 (Shipment Configurations)
          setActiveStep(5);
          sessionStorage.setItem("currentStep", "5");
        }

        // If all data is present, set to last step (Review)
        else {
          setActiveStep(6);
          sessionStorage.setItem("currentStep", "6");
        }
      }
    } catch (error) {
      console.error("Error handling pending product edit:", error);
    }
  }, [isEdit, isPending, selectedProductFormData]);

  // Memoize updateStepData to prevent unnecessary re-renders
  const updateStepData = useCallback(
    (stepName, stepData) => {
      try {
        if (isMounted()) {
          setFormData((prevData) => {
            const updatedData = {
              ...prevData,
              [stepName]: stepData,
            };
            // Persist form data to sessionStorage to prevent data loss on page refresh
            try {
              sessionStorage.setItem(
                "productConfigFormData",
                JSON.stringify(updatedData)
              );
            } catch (error) {
              console.error("Error saving to sessionStorage:", error);
            }
            return updatedData;
          });
        }
      } catch (error) {
        console.error(`Error updating step data for ${stepName}:`, error);
      }
    },
    [isMounted]
  );  // Function to mark API call as successful and handle navigation - to be passed to child components
  const markApiCallSuccessful = useCallback((shouldNavigate = true, nextStepIndex, stepName = null) => {


    // Save current step completion status
    const completedSteps = JSON.parse(
      sessionStorage.getItem("completedSteps") || "[]"
    );
    if (!completedSteps.includes(activeStep)) {
      completedSteps.push(activeStep);
      sessionStorage.setItem(
        "completedSteps",
        JSON.stringify(completedSteps)
      );
    }

    // Determine which step name to use in the message
    const displayStepName = stepName || steps[activeStep];

    // Set snackbar first
    setSnackbar({
      open: true,
      message: `${displayStepName} saved successfully`,
      severity: "success",
    });

    // Navigate if needed
    if (shouldNavigate) {
      const nextStep = typeof nextStepIndex === 'number' ? nextStepIndex : activeStep + 1;
      sessionStorage.setItem("currentStep", nextStep);
      setActiveStep(nextStep);
      // Scroll to top when navigating after successful API call
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep, steps]);

  // Method to update document deliverables data
  const updateAllocationConfigurations = (documents) => {
    setFormData((prevData) => ({
      ...prevData,
      documentDeliverables: documents,
    }));
  };
  const getStepContent = (step) => {
    if (step === undefined || step === null) {
      console.error("Invalid step value");
      return null;
    }

    // Create safe props with error handling
    const commonProps = {
      formData: formData || {},
      updateStepData: (name, data) => {
        try {
          if (typeof updateStepData === "function") {
            updateStepData(name, data);
          } else {
            console.error("updateStepData is not a function");
          }
        } catch (error) {
          console.error("Error calling updateStepData:", error);
        }
      },
      classes: classes || {},
      readOnly: readOnly,
      handleNext,
      handleBack,
      handleSubmit,
      activeStep,
      stepsLength: steps.length,
      selectedProductFormData: location.state?.selectedProductFormData,
      markApiCallSuccessful, // Pass the function to child components
      isProcessFlowConfigEditMode: location.state?.isEdit && !readOnly,
    };

    try {
      switch (step) {
        case 0:
          return <ProductDetails {...commonProps} skipReplicateDialog={skipReplicateDialog} setSkipReplicateDialog={setSkipReplicateDialog} materialDetails={formData?.materialDetails} />;
        case 1:
          return <StatusSelection
            {...commonProps}
            isStepSaved={savedSteps.has(1)}
            onStepSaved={() => setSavedSteps(prev => new Set([...prev, 1]))}
          />;
        case 2:
          return <AllocationConfigurations
            {...commonProps}
            isStepSaved={savedSteps.has(2)}
            onStepSaved={() => setSavedSteps(prev => new Set([...prev, 2]))}
            updateConfigurations={updateAllocationConfigurations}
          />;
        case 3:
          return <OtherConfigurations {...commonProps} />;
        case 4:
          return <ProcessFlowConfiguration {...commonProps} />;
        case 5:
          return <DocumentDeliverables {...commonProps} />; //DocumentDeliverables
        case 6:
          return <ReviewAndAddProduct {...commonProps} skipReplicateDialog={skipReplicateDialog} setSkipReplicateDialog={setSkipReplicateDialog} setActiveStep={setActiveStep} />;
        default:
          return "Unknown step";
      }
    } catch (error) {
      console.error("Error rendering step content:", error);
      return (
        <div>
          <Typography color="error">
            An error occurred while loading this step. Please try again.
          </Typography>
          <Button
            onClick={() => window.location.reload()}
            variant="outlined"
            color="primary"
          >
            Reload
          </Button>
        </div>
      );
    }
  };
  const handleNext = (stepIndex) => {
    try {
      let nextStep;
      if (typeof stepIndex === 'number') {
        nextStep = stepIndex;
      } else {
        nextStep = activeStep + 1;
      }
      // Save current step completion status
      const completedSteps = JSON.parse(
        sessionStorage.getItem("completedSteps") || "[]"
      );
      if (!completedSteps.includes(activeStep)) {
        completedSteps.push(activeStep);
        sessionStorage.setItem(
          "completedSteps",
          JSON.stringify(completedSteps)
        );
      }
      // Save current step to sessionStorage first
      sessionStorage.setItem("currentStep", nextStep);



      // For direct handleNext calls (not from API success), just navigate without showing success message
      if (isMounted()) {
        // Update the active step
        setActiveStep(nextStep);
        // Scroll to top when navigating to next step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Error in handleNext:", error);
      if (isMounted()) {
        setSnackbar({
          open: true,
          message: "An error occurred while proceeding to next step",
          severity: "error",
        });
      }
    }
  };
  const handleBack = () => {
    try {
      if (activeStep === undefined || activeStep <= 0) {
        throw new Error("Already at first step");
      }

      setActiveStep((prevActiveStep) => {
        const newStep = Math.max(0, prevActiveStep - 1);
        // Save current step to sessionStorage
        sessionStorage.setItem("currentStep", newStep);
        // Scroll to top when navigating back
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return newStep;
      });
    } catch (error) {
      console.error("Error in handleBack:", error);
      setSnackbar({
        open: true,
        message: "Cannot go back from first step",
        severity: "error",
      });
    }
  };
  const handleSubmit = async () => {
    try {
      // Handle final submission without validation
      setSnackbar({
        open: true,
        message: "Product configuration submitted successfully",
        severity: "success",
      });

      // Clear session storage after successful submission
      setTimeout(() => {
        sessionStorage.removeItem("productConfigFormData");
        sessionStorage.removeItem("currentStep");
        sessionStorage.removeItem("completedSteps");
        sessionStorage.removeItem("productDetails");

        // Navigate back to ConfigProductDetails (products list)
        history.push(
          `/pcmp/Pharma Co/${roleName}/${organizationName}/${ecosystemId}/products`
        );
      }, 2000); // Small delay to allow the user to see the success message
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "An error occurred during submission",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const checkStepValidity = (step) => {
    return true;
  };

  const handleGoBack = () => {
    // Show confirmation dialog before leaving
    // if (
    //   window.confirm(
    //     "Are you sure you want to go back? Any unsaved changes will be lost."
    //   )
    // ) {
    // Clear session storage when navigating away from the form
    sessionStorage.removeItem("productConfigFormData");
    sessionStorage.removeItem("currentStep");
    sessionStorage.removeItem("completedSteps");
    sessionStorage.removeItem("productDetails");
    sessionStorage.removeItem("productAddedOnce");
    sessionStorage.removeItem('emptyFgStatuses');
    sessionStorage.removeItem('selectedReplicatedFromProductID');
    localStorage.removeItem("fileUploadedProduct");
    localStorage.removeItem("originalCollabrixProductID");


    // Reset snackbar state after going back
    setSnackbar({ open: false, message: '', severity: 'success' });

    // Navigate back
    history.goBack();
    // }
  };

  useEffect(() => {
    // Reset snackbar state on mount
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, []);

  if (hasErrors) {
    return (
      <Box p={3}>
        <Typography color="error">
          Failed to initialize product configuration
        </Typography>
      </Box>
    );
  }

  if (!isInitialized) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  return (
    <div className="config-product-stepper-wrapper">


      <Paper className={classes.root}>
        <Box className={classes.stepperHeader}>
          <Typography className={classes.heading} variant="h5">
            Add New Product
          </Typography>
          <Typography className={classes.subheading}>
            Please add the product details and configurations in the fields below.
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            return (
              <Step key={label} {...stepProps}>
                <StepLabel
                  style={{ whiteSpace: "nowrap" }}
                  className={classes.stepLabel}
                  {...labelProps}
                  StepIconProps={{
                    completed: index < activeStep && (
                      <CheckCircleIcon className={classes.stepCompleted} />
                    ),
                  }}
                >
                  {label}                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div className="stepper-container">{getStepContent(activeStep)}</div>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          key={snackbar.message}
          style={{ zIndex: 9999 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Paper>
    </div>
  );
};

export default function ProductConfig() {
  return (
    <ProductConfigErrorBoundary>
      <ProductConfigStepper />
    </ProductConfigErrorBoundary>
  );
}
